import { Component, OnDestroy, OnInit } from '@angular/core';
import {
  Auth as AngularFireAuth,
  EmailAuthProvider,
  GoogleAuthProvider,
  onAuthStateChanged,
  signInWithRedirect,
} from 'firebase/auth';
import { Inject } from '@angular/core';
import { Router } from '@angular/router';
import { AUTH } from 'src/app/firebase-injection';
import { auth as uiAuth } from 'firebaseui';
import { setUser } from '@sentry/angular';
import { needsEmailVerification } from 'src/app/services/auth.utils';
import { LocalStorageService } from 'src/app/services/local-storage.service';

@Component({
  selector: 'app-user-login',
  templateUrl: './user-login.component.html',
  styleUrls: ['./user-login.component.scss'],
})
export class UserLoginComponent implements OnInit, OnDestroy {
  ui: uiAuth.AuthUI | undefined;
  private readonly googleProvider = new GoogleAuthProvider();
  private signInInProgress = false;
  private unsubscribeAuthState?: () => void;

  constructor(
    @Inject(AUTH) public afAuth: AngularFireAuth,
    private router: Router,
    private localStorage: LocalStorageService
  ) {
    this.unsubscribeAuthState = onAuthStateChanged(this.afAuth, (user) => {
      if (!user || this.signInInProgress) {
        return;
      }
      if (needsEmailVerification(user)) {
        this.router.navigate(['/verify-email']);
      } else {
        this.navigateAfterLogin();
      }
      setUser({
        id: user.uid,
        email: user.email ?? undefined,
        username: user.displayName ?? undefined,
      });
    });
  }
  ngOnDestroy(): void {
    this.unsubscribeAuthState?.();
    this.ui?.delete();
  }

  ngOnInit(): void {
    const pendingInvite = this.localStorage.getInviteLink();

    // FirebaseUI config.
    const uiConfig: uiAuth.Config = {
      // fallback falls firebaseui den callback nicht aufruft.
      signInSuccessUrl: pendingInvite || '/app',
      signInOptions: [
        {
          provider: GoogleAuthProvider.PROVIDER_ID,
          authMethod: 'https://accounts.google.com',
          clientId:
            '468739376893-cskaek4seu5mcfq56rlaftelqp3b3hr1.apps.googleusercontent.com',
        },
        EmailAuthProvider.PROVIDER_ID,
      ],
      tosUrl: '/public/terms-of-service',
      privacyPolicyUrl: '/public/privacy-policy',
      signInFlow: 'popup',
      callbacks: {
        signInSuccessWithAuthResult: () => {
          this.signInInProgress = true;
          const target = this.localStorage.getInviteLink() || '/app';
          this.localStorage.setInviteLink(undefined);
          this.router.navigateByUrl(target);
          return false;
        },
        // Fallback to redirect on browsers where popups are blocked or unsupported (observed on Chrome/Windows).
        signInFailure: (error) => {
          const fallbackErrorCodes = [
            'auth/popup-blocked',
            'auth/cancelled-popup-request',
            'auth/operation-not-supported-in-this-environment',
          ];
          if (fallbackErrorCodes.includes(error.code)) {
            return signInWithRedirect(this.afAuth, this.googleProvider);
          }
          return Promise.reject(error);
        },
      },
    };

    this.ui = new uiAuth.AuthUI(this.afAuth);
    this.ui.start('#firebaseui-auth-container', uiConfig);
  }

  private navigateAfterLogin() {
    const pendingInvite = this.localStorage.getInviteLink();
    if (pendingInvite) {
      this.localStorage.setInviteLink(undefined);
      this.router.navigateByUrl(pendingInvite);
      return;
    }
    this.router.navigate(['/app']);
  }
}
